/**
 * Copyright © 2026 Adnuntius AS.
 */
import * as _ from 'lodash';

const objectStateToMethodMap = {
  'ACTIVE': '$activate',
  'HIDDEN': '$delete',
  'INACTIVE': '$archive'
};

const RESOURCE_METHODS = ['$activate', '$archive', '$delete', '$head'];

export function resolveResourceActionModel(oldModel: any, ResourceFactory?: any): any {
  if (!oldModel) {
    return oldModel;
  }
  if (_.some(RESOURCE_METHODS, (methodName) => _.isFunction(oldModel[methodName]))) {
    return oldModel;
  }
  if (!ResourceFactory || !oldModel.id) {
    return oldModel;
  }
  const instance = new ResourceFactory({id: oldModel.id});
  Object.assign(instance, _.pick(oldModel, ['id', 'objectState', 'name', 'fileName']));
  return instance;
}

export function applyResourceStateFields(target: any, source: any): void {
  if (!target || !source) {
    return;
  }
  if (source.objectState !== undefined) {
    target.objectState = source.objectState;
  }
  if (source.validationWarnings !== undefined) {
    target.validationWarnings = _.cloneDeep(source.validationWarnings);
  }
  if (source.status !== undefined) {
    _.set(target, 'status', source.status);
  }
}

export function applyResourceStateFromApi(target: any, source: any): void {
  if (!target || !source) {
    return;
  }
  applyResourceStateFields(target, source);
  // Match legacy updateObjectState: always replace warnings from the API payload.
  target.validationWarnings = _.cloneDeep(source.validationWarnings || []);
}

export function refreshResourceAfterStateChange(
  oldModel: any,
  modelComms: any,
  ResourceFactory: any,
  initialResult?: any
): any {
  applyResourceStateFromApi(oldModel, initialResult || {});

  if (!ResourceFactory || !oldModel?.id) {
    modelComms.modelUpdated(oldModel);
    return Promise.resolve(oldModel);
  }

  return ResourceFactory.get({id: oldModel.id}).$promise.then((fresh: any) => {
    applyResourceStateFromApi(oldModel, fresh || {});
    modelComms.modelUpdated(oldModel);
    return oldModel;
  }, () => {
    modelComms.modelUpdated(oldModel);
    return oldModel;
  });
}

export function wireModelCommsModelActions(modelComms: any, ResourceFactory?: any): void {
  if (ResourceFactory) {
    modelComms._resourceFactory = ResourceFactory;
  }
  const factory = ResourceFactory || modelComms._resourceFactory;

  modelComms.updateObjectState = function(oldModel: any, objectState: string) {
    const methodName = objectStateToMethodMap[objectState];
    const actionModel = resolveResourceActionModel(oldModel, factory);
    const action = actionModel && actionModel[methodName];
    if (!_.isFunction(action)) {
      return Promise.reject(new Error('Resource is missing ' + methodName));
    }

    // $activate/$archive/$delete take no callback - they return a Promise
    // resolving to the updated resource. Chain the modelComms refresh onto
    // that promise so subscribers actually learn about the new objectState.
    return action.call(actionModel).then((aResource: any) => {
      return refreshResourceAfterStateChange(oldModel, modelComms, factory, aResource || {});
    });
  };

  modelComms.updateObjectId = function(oldModel: any, newId: string, hasDuplicateCallback: (resp: any) => void, isUniqueCallback: () => void) {
    const headModel = resolveResourceActionModel(_.clone(oldModel), factory);
    headModel.id = newId;
    headModel.$head(function(resp: any) {
      hasDuplicateCallback(resp);
    }).catch(function() {
      isUniqueCallback();
      oldModel.id = newId;
      modelComms.modelUpdated(oldModel);
    });
  };
}
